import { COMPANY } from '@/content/company';
import { captchaConfigured } from '@/content/recaptcha';

export interface Enquiry {
  name: string;
  company: string;
  email: string;
  /** One of the "Area of interest" options. */
  interest: string;
  description: string;
}

/**
 * Bot signals collected by the form itself, not by the visitor.
 *
 * The first two are not hard gates. The backend stores a flagged submission and
 * simply does not email it, so a false positive is recoverable - it sits in the
 * database marked as spam rather than vanishing. The captcha token is different:
 * once a site sets `captcha_required`, a missing or low-scoring token is refused
 * outright.
 */
export interface EnquiryGuard {
  /** A field no human can see or tab to. Anything in it means a bot filled it. */
  honeypot: string;
  /**
   * Milliseconds from the form appearing to the visitor pressing send.
   *
   * Undefined if it could not be measured, in which case it is left out of the
   * request entirely rather than sent as a guess - the backend reads a missing
   * value as "not a bot", so an unmeasurable form still submits.
   */
  elapsedMs?: number;
  /**
   * A single-use reCAPTCHA v3 token, verified server-side against Google.
   *
   * Null when no site key is configured, or when Google could not be reached -
   * see src/lib/recaptcha.ts for why that is a null rather than a thrown error.
   * Omitted from the request when absent, so the backend can tell "no captcha
   * here" apart from "a token that failed".
   */
  captchaToken?: string | null;
}

export type EnquiryResult = { ok: true } | { ok: false; error: string };

/**
 * Same-origin on purpose.
 *
 * Netlify rewrites this path to the shared-web-forms Supabase Edge Function
 * (see netlify.toml). Posting to *.supabase.co directly would be a third-party
 * request from the visitor's browser, which would mean loosening
 * `connect-src 'self'` in the CSP and undoing a guarantee the site makes
 * deliberately. The rewrite keeps the browser talking only to skabro.eu and
 * keeps the backend URL out of the page source.
 *
 * `npm run dev` and `npm run preview` proxy the same path - see vite.config.ts
 * and scripts/serve.mjs - so the form behaves identically in all three.
 */
const ENDPOINT = '/api/forms/submit';

/** Identifies this website and form to the shared backend. */
const SITE_KEY = 'skabro';
const FORM_KEY = 'contact';

/** Long enough to cover a slow network, short enough not to hang the button. */
const TIMEOUT_MS = 15_000;

const FALLBACK = `Something went wrong sending that. Please email us at ${COMPANY.email} and we will pick it up.`;

export async function submitEnquiry(enquiry: Enquiry, guard: EnquiryGuard): Promise<EnquiryResult> {
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site: SITE_KEY,
        form: FORM_KEY,
        payload: {
          name: enquiry.name.trim(),
          company: enquiry.company.trim(),
          email: enquiry.email.trim(),
          interest: enquiry.interest,
          description: enquiry.description.trim(),
        },
        honeypot: guard.honeypot,
        ...(guard.elapsedMs === undefined ? {} : { elapsed_ms: guard.elapsedMs }),
        ...(guard.captchaToken
          ? { captcha_token: guard.captchaToken }
          : /*
             * Says "this page has a captcha and could not get a token" as distinct
             * from "this page has no captcha". Without it the two are identical on
             * the wire - a token that never arrived leaves no trace anywhere,
             * server-side or client-side, which made a silent captcha failure
             * genuinely hard to tell apart from a working shadow mode.
             */
            captchaConfigured()
            ? { captcha_unavailable: true }
            : {}),
        page_url: window.location.href,
      }),
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });

    /*
     * The backend answers with a human-readable `error` on every rejection -
     * "Please fill in: email.", "reached its limit for today", and so on - so
     * prefer its wording over anything invented here. The caller renders it in
     * an alert region verbatim.
     */
    const body: unknown = await response.json().catch(() => null);
    const message =
      body !== null &&
      typeof body === 'object' &&
      typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null;

    if (!response.ok) {
      return { ok: false, error: message ?? FALLBACK };
    }

    return { ok: true };
  } catch {
    /*
     * Offline, DNS failure, or the 15s timeout. Deliberately does not
     * distinguish between them: none of it is actionable for the visitor, and
     * the useful next step is the same in every case.
     */
    return {
      ok: false,
      error: `We could not reach the server. Check your connection and try again, or email us at ${COMPANY.email}.`,
    };
  }
}
